import { useQuery } from "@tanstack/react-query";
import { SectionHeading } from "@/components/bespoke/section-heading";
import type { Wallet } from "@zworks/console-contracts";
import { BudgetOrbit } from "@/components/bespoke";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { monthlyLedger } from "@/lib/monthly-ledger";
import { formatCreditsUsd } from "@/lib/format";
import { dailySpend } from "@/lib/ledger";

export function UsageSpend({ projectId, wallet }: { projectId: string; wallet: Wallet }) {
  const now = new Date();
  const month = now.toISOString().slice(0, 7);
  const spend = useQuery({
    queryKey: ["projects", projectId, "monthly-ledger", month],
    queryFn: ({ signal }) => monthlyLedger(projectId, now, signal),
  });
  const days = dailySpend(spend.data ?? [], now, now.getUTCDate());
  const maximum = Math.max(1, ...days.map((day) => day.credits));
  const cumulative = days.reduce<number[]>(
    (totals, day) => [...totals, (totals.at(-1) ?? 0) + day.credits],
    [],
  );
  const running = cumulative.at(-1) ?? 0;
  const ceiling = Math.max(1, wallet.monthlyLimit, ...cumulative);
  const points = cumulative
    .map(
      (credits, index) =>
        `${20 + (index * 580) / Math.max(1, days.length - 1)},${150 - (credits / ceiling) * 130}`,
    )
    .join(" ");
  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
      <Card>
        <CardHeader>
          <CardTitle>Spend against the monthly limit</CardTitle>
        </CardHeader>
        <CardContent>
          {spend.isPending ? (
            <p role="status">Loading monthly spend…</p>
          ) : spend.isError ? (
            <div role="alert">
              Monthly spend is unavailable.{" "}
              <Button variant="outline" size="sm" onClick={() => void spend.refetch()}>
                Retry
              </Button>
            </div>
          ) : (
            <>
              <svg
                viewBox="0 0 620 175"
                className="h-40 w-full"
                role="img"
                aria-label={`Cumulative confirmed spend this month: ${formatCreditsUsd(running)}; monthly limit ${formatCreditsUsd(wallet.monthlyLimit)}`}
              >
                <line
                  x1="20"
                  y1={150 - (wallet.monthlyLimit / ceiling) * 130}
                  x2="600"
                  y2={150 - (wallet.monthlyLimit / ceiling) * 130}
                  stroke="currentColor"
                  strokeDasharray="4 4"
                  className="text-muted-foreground"
                />
                <polyline points={points} fill="none" stroke="var(--chart)" strokeWidth="3" />
                <text x="20" y="173" className="fill-muted-foreground text-xs">
                  {days[0]?.label}
                </text>
                <text x="600" y="173" textAnchor="end" className="fill-muted-foreground text-xs">
                  {days.at(-1)?.label}
                </text>
              </svg>
              <p className="text-xs text-muted-foreground">
                Confirmed consumption in UTC · Dashed line: {formatCreditsUsd(wallet.monthlyLimit)}{" "}
                limit
              </p>
            </>
          )}
          <details className="mt-3">
            <summary className="cursor-pointer text-sm">Daily spend</summary>
            {spend.isSuccess ? (
              <table className="mt-2 w-full text-sm">
                <caption className="sr-only">Daily confirmed spend in UTC</caption>
                <thead>
                  <tr>
                    <th scope="col" className="text-left">
                      Date
                    </th>
                    <th scope="col" className="text-right">
                      Spend
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {days.map((day) => (
                    <tr key={day.dayStart}>
                      <td>{day.label}</td>
                      <td className="text-right tabular-nums">{formatCreditsUsd(day.credits)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="text-sm text-muted-foreground">
                Daily figures appear when the monthly ledger is available.
              </p>
            )}
          </details>
          <SectionHeading className="mt-3">Daily spend</SectionHeading>
          {spend.isSuccess ? (
            <svg
              viewBox="0 0 620 60"
              className="h-16 w-full"
              role="img"
              aria-label="Daily confirmed spend bars, values in the daily spend table"
            >
              {days.map((day, index) => (
                <rect
                  key={day.dayStart}
                  x={(index * 620) / days.length}
                  y={55 - (day.credits / maximum) * 50}
                  width={Math.max(1, 620 / days.length - 3)}
                  height={(day.credits / maximum) * 50}
                  fill="var(--chart)"
                >
                  <title>
                    {day.label}: {formatCreditsUsd(day.credits)}
                  </title>
                </rect>
              ))}
            </svg>
          ) : null}
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Budget orbit</CardTitle>
        </CardHeader>
        <CardContent>
          <BudgetOrbit
            used={wallet.monthlyUsed}
            projected={wallet.estimated}
            reserved={wallet.reserved}
            limit={wallet.monthlyLimit}
          />
        </CardContent>
      </Card>
    </div>
  );
}
